import React, { Children, isValidElement, useEffect, useMemo, useState } from 'react'
import { Box, IconButton, Tabs, useTheme } from '@mui/material'
import type { TabsProps } from '@mui/material/Tabs'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useIsMobile } from 'hooks/screen/useIsMobile'

const DEFAULT_VISIBLE = 4

/** Undo AdminPageFrame pill/capsule selected-tab chrome. Dashboard tabs use a line. */
export const dashboardTabsSx = {
  '& .MuiTabs-indicator': {
    display: 'block',
    height: 2,
    borderRadius: 0,
  },
  '&& .MuiTab-root': {
    minHeight: 44,
    py: 0.5,
    textTransform: 'none',
    fontSize: '0.8125rem',
    borderRadius: 0,
    bgcolor: 'transparent',
    color: 'text.secondary',
    '&:hover': {
      bgcolor: 'transparent',
      color: 'primary.main',
    },
    '&.Mui-selected': {
      bgcolor: 'transparent',
      color: 'primary.main',
      '&:hover': {
        bgcolor: 'transparent',
        color: 'primary.main',
      },
    },
  },
} as const

export interface DashboardPagedTabsProps {
  value: number
  onChange: (event: React.SyntheticEvent, value: number) => void
  children: React.ReactNode
  'aria-label'?: string
  /** How many tabs to show at once on mobile. Default 4. */
  visibleCount?: number
  tabsSx?: TabsProps['sx']
}

/**
 * On mobile with more than `visibleCount` tabs, shows a window of tabs
 * with left/right arrows. Desktop (and short lists) stay fullWidth.
 */
export const DashboardPagedTabs: React.FC<DashboardPagedTabsProps> = ({
  value,
  onChange,
  children,
  'aria-label': ariaLabel = 'dashboard tabs',
  visibleCount = DEFAULT_VISIBLE,
  tabsSx,
}) => {
  const isMobile = useIsMobile()
  const theme = useTheme()
  const tabs = useMemo(
    () => Children.toArray(children).filter(isValidElement),
    [children],
  )
  const tabCount = tabs.length
  const needsPaging = isMobile && tabCount > visibleCount

  const [windowStart, setWindowStart] = useState(0)
  const maxStart = Math.max(0, tabCount - visibleCount)

  // Keep the selected tab inside the visible window (e.g. after swipe).
  useEffect(() => {
    if (!needsPaging) return
    setWindowStart((start) => {
      if (value < start) return value
      if (value >= start + visibleCount) return value - visibleCount + 1
      return Math.min(start, maxStart)
    })
  }, [value, needsPaging, visibleCount, maxStart])

  useEffect(() => {
    setWindowStart((start) => Math.min(start, maxStart))
  }, [maxStart])

  if (!needsPaging) {
    return (
      <Tabs
        value={value}
        onChange={onChange}
        variant='fullWidth'
        aria-label={ariaLabel}
        sx={{
          maxWidth: '100%',
          ...dashboardTabsSx,
          '& .MuiTabs-flexContainer': {
            justifyContent: 'center',
          },
          ...((tabsSx as object) || {}),
        }}
      >
        {tabs}
      </Tabs>
    )
  }

  const end = windowStart + visibleCount
  const visibleTabs = tabs.slice(windowStart, end)
  const localValue = value - windowStart
  const canGoLeft = windowStart > 0
  const canGoRight = windowStart < maxStart

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <IconButton
        aria-label='Show previous tabs'
        size='small'
        disabled={!canGoLeft}
        onClick={() => setWindowStart((s) => Math.max(0, s - 1))}
        sx={{
          alignSelf: 'center',
          flexShrink: 0,
          color: theme.palette.primary.main,
          '&.Mui-disabled': { opacity: 0.3 },
        }}
      >
        <ChevronLeftIcon />
      </IconButton>

      <Tabs
        value={localValue}
        onChange={(event, localIndex: number) => {
          onChange(event, localIndex + windowStart)
        }}
        variant='fullWidth'
        aria-label={ariaLabel}
        sx={{
          flex: 1,
          minWidth: 0,
          maxWidth: '100%',
          ...dashboardTabsSx,
          '& .MuiTabs-flexContainer': {
            justifyContent: 'space-between',
          },
          '& .MuiTab-root': {
            minWidth: 0,
            flex: 1,
            px: 0.5,
          },
          ...((tabsSx as object) || {}),
        }}
      >
        {visibleTabs}
      </Tabs>

      <IconButton
        aria-label='Show next tabs'
        size='small'
        disabled={!canGoRight}
        onClick={() => setWindowStart((s) => Math.min(maxStart, s + 1))}
        sx={{
          alignSelf: 'center',
          flexShrink: 0,
          color: theme.palette.primary.main,
          '&.Mui-disabled': { opacity: 0.3 },
        }}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  )
}

export default DashboardPagedTabs
